import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { useRef, useState } from 'react'
import { useTheme, useWorkspace } from '@opallabs/hooks'
import { DotLoader, Modal, toast } from '@opallabs/lapidary'
import type {
  SupportedResource,
  ResourceDataParams,
  ResourceShareParams,
  ResourceShareModalProps,
  ResourceShareModalHandlers,
} from '../types'
import { ConfirmationBody } from './bodies/ConfirmationBody'
import { ResourceShareBody } from './bodies/ResourceShareBody'
import * as styles from './styles'
import { supportedResourceMeta } from './supportedResources'
import { ResourcePrivacyToast } from './toasts/ResourcePrivacyToast'

export { ResourcePrivacyToast, supportedResourceMeta }

type RequiredShareParams<ResourceType extends SupportedResource> = Required<
  Omit<ResourceShareParams<ResourceType>, 'resourceId' | 'workspace'>
>

type SingleResourceShareModalProps<ResourceType extends SupportedResource> =
  ResourceDataParams &
    ResourceShareModalProps &
    ResourceShareModalHandlers & {
      resourceShareParams: RequiredShareParams<ResourceType>
      resourceType: ResourceType
    }

export const ResourceShareModal = <T extends SupportedResource>({
  className,
  effectivePrivacy: effectivePrivacyProp,
  isVisible,
  metadataQuery,
  onChangeAccess,
  onClose,
  ownerId,
  readOnly,
  resourceId,
  resourceShareParams,
  resourceTitle,
  resourceType,
  resourceDisplayType,
}: SingleResourceShareModalProps<T>) => {
  const theme = useTheme()
  const currentWorkspace = useWorkspace()
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [canEscapeClose, setCanEscapeClose] = useState(true)
  const closeButtonRef = useRef<HTMLButtonElement>(null)

  const {
    data: accessMetadata,
    isFetching,
    isFetchedAfterMount,
  } = metadataQuery
  const effectivePrivacy =
    effectivePrivacyProp ?? accessMetadata?.effectivePrivacy ?? null
  const resourceMeta = supportedResourceMeta[resourceType]

  const handleClose = ({ changesSaved }: { changesSaved: boolean }) => {
    onClose()
    setShowConfirmation(false)
    if (changesSaved) {
      toast(
        (props) => (
          <ResourcePrivacyToast<typeof resourceType>
            {...props}
            resourceMeta={resourceMeta}
            resourceShareParams={{
              resourceId,
              workspace: currentWorkspace,
              ...resourceShareParams,
            }}
          />
        ),
        {
          type: 'success',
          toastId: 'resource-privacy-toast',
          autoClose: 5000,
          closeOnClick: true,
        }
      )
    }
  }

  const shareModalClose = () => {
    if (canEscapeClose) {
      handleClose({ changesSaved: false })
    } else {
      setShowConfirmation(true)
      setCanEscapeClose(true)
    }
  }

  const confirmationModalClose = () => {
    setCanEscapeClose(false)
    setShowConfirmation(false)
  }

  return (
    <Modal.Root open={isVisible}>
      <Modal.Content
        className={className}
        key={resourceId}
        css={styles.modal}
        aria-describedby={undefined}
        onEscapeKeyDown={() => {
          if (canEscapeClose) {
            handleClose({ changesSaved: false })
          } else {
            setShowConfirmation(true)
            setCanEscapeClose(false)
          }
        }}
      >
        <Modal.Close
          ref={closeButtonRef}
          onClick={showConfirmation ? confirmationModalClose : shareModalClose}
        />
        {isFetching && !isFetchedAfterMount ? (
          <div
            css={styles.loaderContainer}
            style={{ inlineSize: 580, minBlockSize: 406, paddingBlockEnd: 48 }}
          >
            <VisuallyHidden asChild>
              <Modal.Title>Loading...</Modal.Title>
            </VisuallyHidden>
            <DotLoader />
          </div>
        ) : (
          <ResourceShareBody<typeof resourceType>
            accessMetadata={accessMetadata}
            css={{
              display: showConfirmation ? 'none' : undefined,
            }}
            effectivePrivacy={effectivePrivacy}
            hideContent={showConfirmation}
            readOnly={readOnly}
            resourceId={resourceId}
            resourceMeta={resourceMeta}
            resourceShareParams={resourceShareParams}
            resourceTitle={resourceTitle}
            resourceDisplayType={resourceDisplayType}
            onChangeAccess={onChangeAccess}
            onClose={handleClose}
            ownerId={ownerId}
            setCanEscapeClose={setCanEscapeClose}
            theme={theme}
          />
        )}

        {showConfirmation && (
          <ConfirmationBody
            closeConfirmation={() => {
              setCanEscapeClose(false)
              setShowConfirmation(false)
            }}
            discardAndClose={() => handleClose({ changesSaved: false })}
          />
        )}
      </Modal.Content>
    </Modal.Root>
  )
}
